import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import dayjs from 'dayjs';
import { Info, LocateFixed, RouteIcon } from 'lucide-react';

import { Accordion } from 'components/Accordion';
import { Button } from 'components/Button';
import { Timeline } from 'components/CheckCallTimeline';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import DateTimeInput from 'components/input/DateTimeInput';
import { RHFTextInput } from 'components/input/RHFTextInput';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useLoadInvalidation } from 'hooks/useLoadContext';
import useLogPostHogPageView from 'hooks/useLogPostHogPageView';
import useTMSContext from 'hooks/useTMSContext';
import { useToast } from 'hooks/useToaster';
import { getCheckCallHistory } from 'lib/api/getCheckCallHistory';
import { skipSuggestion } from 'lib/api/skipSuggestion';
import { SubmitCheckCallProps, submitCheckCall } from 'lib/api/submitCheckCall';
import { CheckCall } from 'types/CheckCall';
import { NormalizedLoad } from 'types/Load';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';
import ButtonText from 'types/enums/ButtonText';
import CheckCallSource from 'types/enums/CheckCallSource';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { TMS } from 'types/enums/Integrations';
import Pageview from 'types/enums/Pageview';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import {
  CheckCallSuggestion,
  SuggestionStatus,
} from 'types/suggestions/LoadSuggestions';
import { applyCompleteKitSuggestions } from 'utils/completeKitSuggestion';
import { titleCase } from 'utils/formatStrings';
import { normalizeDateForTMS } from 'utils/parseDatesForTMSForm';
import { normalizeUTCDate } from 'utils/time';

import { LoadSectionAccordionItem } from '../LoadInformation/Components';
import {
  CheckCallInputs,
  CheckCallTextInputProps,
  aljexStatusToLoadTimestampMapping,
  relaySourceEnums,
  statusMapping,
  tmsToIsMilestoneMap,
  tmsToReasonEnumsMap,
  tmsToStatusEnumsMap,
} from './types';
import {
  requireCityStateByTMS,
  showExceptionFieldByTMS,
  showNextStopETAByTMS,
  showOnTimeFieldByTMS,
  showOutDatetimeByTMS,
  showReasonFieldByTMS,
} from './util';

type CheckCallSectionProps = {
  normalizedLoad: NormalizedLoad;
  suggestions: CheckCallSuggestion[];
};

const CheckCallTextInput = (props: CheckCallTextInputProps) => (
  <RHFTextInput {...props} />
);

export default function CheckCallSection({
  normalizedLoad: load,
  suggestions,
}: CheckCallSectionProps) {
  useLogPostHogPageView(Pageview.CheckCalls, {
    service_id: load.serviceID,
    load_id: load.ID,
    freightTrackingID: load.freightTrackingID,
  });

  const { tmsName } = useTMSContext();
  const invalidateLoad = useLoadInvalidation();
  const isAljex = tmsName === TMS.Aljex;
  const isRelay = tmsName === TMS.Relay;
  const isTZAgnosticTMS = isAljex || isRelay;
  const isStark = tmsName === TMS.Stark;

  // TMS-specific variables
  const statusEnums = tmsToStatusEnumsMap[tmsName as TMS] || [];
  const reasonEnums = tmsToReasonEnumsMap[tmsName as TMS] || [];

  const isMilestoneFunc = (status: Maybe<string>): boolean => {
    const func = tmsToIsMilestoneMap[tmsName as TMS];
    if (!func) return false;
    return func(status);
  };

  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [curSuggestion, setCurSuggestion] = useState<
    Maybe<CheckCallSuggestion>
  >(suggestions.length > 0 ? suggestions[suggestionIndex] : null);

  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [skipLoading, setSkipLoading] = useState(false);
  const [activeTabs, setActiveTabs] = useState<string[]>(
    suggestions.length > 0 ? ['submitCheckCall'] : []
  );
  const [history, setHistory] = useState<Array<CheckCall>>([]);
  const [isMilestone, setIsMilestone] = useState<boolean>(
    isMilestoneFunc(curSuggestion?.status as string)
  );
  const [enableOutDatetimeField, setEnableOutDatetimeField] = useState(false);
  const [enableNextStopETAField, setEnableNextStopETAField] = useState(false);
  const [enableReasonField, setEnableReasonField] = useState(false);
  const [enableExceptionField, setEnableExceptionField] = useState(false);
  const [enableOnTimeField, setEnableOnTimeField] = useState(false);
  const [requireCityState, setRequireCityState] = useState(true);

  const fetchCheckCalls = async (loadID: number) => {
    const res = await getCheckCallHistory(loadID);
    if (res.isOk()) {
      setHistory(res.value ?? []);
    }
  };

  const {
    currentState: { clickedSuggestion },
    setCurrentState,
  } = useContext(SidebarStateContext);

  // Expand submit section when a Kit suggestion is selected into this form.
  useEffect(() => {
    if (!activeTabs.includes('submitCheckCall')) {
      setActiveTabs((oldTabs) => [...oldTabs, 'submitCheckCall']);
    }
  }, [clickedSuggestion]);

  useEffect(() => {
    if (load.ID) {
      fetchCheckCalls(load.ID);
    }
  }, [load.ID]);

  const getStatusFromMapping = (
    suggestedStatus: MaybeUndef<string>,
    integration: string
  ): Maybe<string> => {
    if (!suggestedStatus) {
      return null;
    }

    const mappedValue =
      statusMapping[suggestedStatus.toLowerCase()]?.[
        integration as keyof (typeof statusMapping)[string]
      ];

    if (typeof mappedValue === 'object') {
      return mappedValue?.value || null;
    } else if (typeof mappedValue === 'string') {
      return mappedValue;
    }

    return null;
  };

  const defaultValues = useMemo<CheckCallInputs>(() => {
    const parsedValues = {
      freightTrackingID: load.freightTrackingID,

      // NOTE: For dev, remove
      // city: 'Boston',
      // state: 'MA',
      // text: 'beep bop boop',
      // status: 'On Time',
      // reason: '',
      nextStopID: '',
      source: curSuggestion && isRelay ? CheckCallSource.Dispatcher : undefined,
      city: curSuggestion?.suggested.checkCallChanges.city || '',
      state: curSuggestion?.suggested.checkCallChanges.state || '',
      dateTime: curSuggestion?.suggested.checkCallChanges.timestamp
        ? normalizeDateForTMS(
            new Date(curSuggestion.suggested.checkCallChanges.timestamp),
            tmsName
          )
        : null,
      status:
        getStatusFromMapping(
          curSuggestion?.suggested.checkCallChanges.status,
          tmsName
        ) || '',
      reason: curSuggestion && tmsName === TMS.Aljex ? 'Normal Status' : '',
      notes: !(isMilestone && isAljex)
        ? curSuggestion?.suggested.checkCallChanges.notes || ''
        : '',
    };

    return parsedValues as CheckCallInputs;
  }, [load, curSuggestion]);

  const formMethods = useForm<CheckCallInputs>({ defaultValues });
  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    getValues,
    setValue,
    resetField,
  } = formMethods;

  // Set default date and reason if no suggestions without AI-filled label
  useEffect(() => {
    reset(defaultValues);

    if (defaultValues.dateTime === null) {
      setValue('dateTime', new Date());
    }

    if (!defaultValues.reason && isAljex) {
      setValue('reason', 'Normal Status');
    }
  }, [defaultValues]);

  // Reset suggestion and index when array of pending suggestions updated
  useEffect(() => {
    if (
      clickedSuggestion?.pipeline === SuggestionPipelines.CheckCall &&
      clickedSuggestion.status !== SuggestionStatus.Accepted
    ) {
      const idx = suggestions.findIndex((s) => s.id === clickedSuggestion.id);
      if (idx >= 0) {
        setSuggestionIndex(idx);
        setCurSuggestion(suggestions[idx]);
        return;
      }
    }

    setSuggestionIndex(0);
    setCurSuggestion(suggestions[0] ?? null);
  }, [suggestions, clickedSuggestion]);

  useEffect(() => {
    if (suggestionIndex >= 0 && suggestionIndex < suggestions.length) {
      setCurSuggestion(suggestions[suggestionIndex]);
    } else {
      setCurSuggestion(null);
    }
  }, [suggestionIndex]);

  const watchedStatus = watch('status');

  // Dynamically display/validate form inputs based on status pt. 1
  useEffect(() => {
    setIsMilestone(isMilestoneFunc(watchedStatus));
    const localEnableNextStop = showNextStopETAByTMS(tmsName, watchedStatus);
    setEnableNextStopETAField(localEnableNextStop);
    setEnableReasonField(showReasonFieldByTMS(tmsName, watchedStatus));
    setEnableExceptionField(showExceptionFieldByTMS(tmsName, watchedStatus));
    const localEnableOutDT = showOutDatetimeByTMS(tmsName, watchedStatus);
    setEnableOutDatetimeField(localEnableOutDT);
    if (localEnableOutDT) {
      setValue('outDatetimeWithoutTimezone', new Date());
    }

    // NFI Feature request: they first submit an "arrival at stop" CC, then "mark loaded/delivered",
    // which requires in and out datetimes. We AI-fill the in datetime based on the previous
    // "arrival at stop" timestamp
    if (isRelay) {
      // If dispatching driver, ETA is likely for pickup
      if (
        watchedStatus?.toLowerCase().includes('dispatch') &&
        localEnableNextStop
      ) {
        setValue('nextStopID', 'pickup');
      } else {
        setValue('nextStopID', '');
      }

      // If not "mark loaded" || "mark delivered", reset date and remove AI-fill
      if (!localEnableOutDT) {
        setValue('dateTime', new Date(), { shouldDirty: true });
        return;
      }
      const arrivedCC = history
        .filter(
          (call) =>
            call.status.toLowerCase() === 'arrival at stop' &&
            // If user clicked 'mark loaded', then search for pickup arrival (stop 1)
            // If user clicked 'mark delivered', then search for dropoff arrival (stop 2)
            call.city
              ?.toLowerCase()
              .includes(
                watchedStatus?.toLowerCase().includes('loaded')
                  ? 'stop 1'
                  : 'stop 2'
              )
        )
        .reduce((mostRecent: Maybe<CheckCall>, current: CheckCall) => {
          if (
            !mostRecent ||
            dayjs(current.dateTimeWithoutTimezone).isAfter(
              dayjs(mostRecent.dateTimeWithoutTimezone)
            )
          ) {
            return current;
          }
          return mostRecent;
        }, null);

      // If no previous "arrival at stop" found, reset date and remove AI-fill
      if (!arrivedCC || !dayjs(arrivedCC.dateTimeWithoutTimezone).isValid()) {
        setValue('dateTime', new Date(), { shouldDirty: true });
        return;
      }

      resetField('dateTime', {
        defaultValue: normalizeUTCDate(
          new Date(arrivedCC.dateTimeWithoutTimezone)
        ),
      });
    }
  }, [watchedStatus]);

  // Dynamically display/validate form inputs based on status pt. 2
  useEffect(() => {
    setRequireCityState(
      requireCityStateByTMS(tmsName, watchedStatus, isMilestone)
    );
    setEnableOnTimeField(
      showOnTimeFieldByTMS(tmsName, watchedStatus, isMilestone)
    );
  }, [isMilestone, watchedStatus]);

  const onSubmitCheckCall: SubmitHandler<CheckCallInputs> = async (data) => {
    setLoading(true);

    const cleanedData = data as unknown as SubmitCheckCallProps;

    const res = await submitCheckCall({
      ...cleanedData,
      isOnTime:
        data.isOnTime?.toLowerCase() === 'yes'
          ? true
          : data.isOnTime?.toLowerCase() === 'no'
            ? false
            : null,
      isException:
        data.isException?.toLowerCase() === 'yes'
          ? true
          : data.isException?.toLowerCase() === 'no'
            ? false
            : null,
      tmsName: tmsName,
      loadID: load.ID!,
      ID: 0,
      userTimezone: dayjs.tz.guess(),
      suggestion: curSuggestion,
    });

    if (res.isOk()) {
      if (res.value.history && res.value.history.length > 0) {
        setHistory(res.value.history);
      }
      toast({
        description: res.value.message || 'Successfully submitted check call',
        variant: 'success',
      });

      // Mark Accepted, advance to next pending Check Call in Kit, arm scroll-to-top if needed
      if (curSuggestion?.id != null) {
        setCurrentState((prevState) => ({
          ...prevState,
          ...applyCompleteKitSuggestions(
            prevState,
            curSuggestion.id,
            SuggestionPipelines.CheckCall
          ),
        }));
      }
    } else {
      toast({
        description: res.error.message,
        variant: 'destructive',
      });
    }

    // Invalidate load async so user sees updated data in Load Info tab
    invalidateLoad();
    setLoading(false);
  };

  const handleSkipSuggestion = async () => {
    setSkipLoading(true);

    const res = await skipSuggestion(curSuggestion!.id);

    if (res.isOk()) {
      toast({
        description: 'Suggestion skipped.',
        variant: 'default',
      });
    } else {
      toast({
        description: res.error.message,
        variant: 'destructive',
      });
    }

    setSkipLoading(false);
  };

  return (
    <Accordion type='multiple' value={activeTabs} onValueChange={setActiveTabs}>
      {!isStark && (
        <LoadSectionAccordionItem
          label='Submit Check Call'
          icon={<LocateFixed className='h-6 w-6' strokeWidth={1} />}
          name='submitCheckCall'
          activeTabs={activeTabs}
        >
          <div>
            {isMilestone && load.moreThanTwoStops && (
              <span className='flex flex-row bg-info-200 rounded-lg space-x-1 py-1 mt-0'>
                <div>
                  <Info
                    className='h-4 w-4 pl-1'
                    color='#666666'
                    strokeWidth={3}
                  />
                </div>
                <span className='text-xs text-neutral-600 font-medium'>
                  {`For multi-stop loads, pickup/dropoff related check calls are supported only for the first pickup and last dropoff locations.
                  More support for multi-stop loads coming soon!`}
                </span>
              </span>
            )}
            <ExtendedFormProvider aiDefaultValues={true}>
              <FormProvider {...formMethods}>
                <div>
                  <form
                    onSubmit={handleSubmit(onSubmitCheckCall)}
                    className='grid gap-4 grid-cols-1 mt-0 mx-0 w-full'
                  >
                    {isRelay && (
                      <div>
                        <Label name='source' required>
                          Source
                        </Label>
                        <Controller
                          name='source'
                          control={control}
                          rules={{ required: 'Required' }}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <SelectTrigger className='w-full mt-2'>
                                <SelectValue placeholder='Choose' />
                              </SelectTrigger>
                              <SelectContent>
                                {relaySourceEnums.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {titleCase(option.value)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`source`}
                          render={({ message }: { message: string }) => (
                            <Typography
                              variant='body-xs'
                              className='text-error-500'
                            >
                              {message}
                            </Typography>
                          )}
                        />
                      </div>
                    )}

                    {statusEnums.length > 0 && (
                      <div>
                        <Label name='status' required>
                          Status
                        </Label>
                        <Controller
                          name='status'
                          control={control}
                          rules={{
                            required:
                              statusEnums.length > 0 ? 'Required' : undefined,
                          }}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <SelectTrigger className='w-full mt-1'>
                                <SelectValue placeholder='Choose' />
                              </SelectTrigger>
                              <SelectContent>
                                {statusEnums.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.value}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`status`}
                          render={({ message }: { message: string }) => (
                            <Typography
                              variant='body-xs'
                              className='text-error-500'
                            >
                              {message}
                            </Typography>
                          )}
                        />
                      </div>
                    )}

                    {requireCityState && (
                      <CheckCallTextInput
                        name='city'
                        label='City'
                        required={requireCityState}
                        readOnly={!requireCityState}
                      />
                    )}

                    {requireCityState && (
                      <CheckCallTextInput
                        name='state'
                        label='State'
                        required={requireCityState}
                        readOnly={!requireCityState}
                      />
                    )}

                    <DateTimeInput
                      name='dateTime'
                      label={`${enableOutDatetimeField ? 'In ' : ''}Date and Time`}
                      control={control}
                      required
                      preventNormalizedLabelTZ={isTZAgnosticTMS}
                    />

                    {enableOutDatetimeField && (
                      <DateTimeInput
                        name='outDatetimeWithoutTimezone'
                        label='Out Date and Time'
                        control={control}
                        preventNormalizedLabelTZ={isTZAgnosticTMS}
                      />
                    )}

                    {isAljex && isMilestone && (
                      <span className='flex flex-row bg-info-200 rounded-lg space-x-1 py-1 mt-0'>
                        <div>
                          <Info
                            className='h-4 w-4 pl-1'
                            color='#666666'
                            strokeWidth={3}
                          />
                        </div>
                        <span className='text-xs text-neutral-600 font-medium'>
                          {`This updates ${
                            aljexStatusToLoadTimestampMapping[
                              getValues('status')?.toLowerCase()
                            ]
                          }.`}
                        </span>
                      </span>
                    )}

                    {enableNextStopETAField && (
                      <div>
                        <Label name='nextStopID' required>
                          {'ETA for Stop'}
                        </Label>
                        <Controller
                          name='nextStopID'
                          control={control}
                          rules={{ required: 'Required' }}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <SelectTrigger className='w-full mt-2'>
                                <SelectValue placeholder='Choose' />
                              </SelectTrigger>
                              <SelectContent>
                                {/* TODO: Support multi-stop loads */}
                                <SelectItem key={'pickup'} value={'pickup'}>
                                  {`Pickup (${load.pickup.name}, ${load.pickup.addressLine1}, ${load.pickup.city}, ${load.pickup.state})`}
                                </SelectItem>
                                <SelectItem key={'dropoff'} value={'dropoff'}>
                                  {`Dropoff (${load.consignee.name}, ${load.consignee.addressLine1}, ${load.consignee.city}, ${load.consignee.state})`}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`nextStopID`}
                          render={({ message }: { message: string }) => (
                            <Typography
                              variant='body-xs'
                              className='text-error-500'
                            >
                              {message}
                            </Typography>
                          )}
                        />
                      </div>
                    )}

                    {enableNextStopETAField && (
                      <DateTimeInput
                        name='nextStopETAWithoutTimezone'
                        label='Next Stop ETA'
                        control={control}
                        required
                        preventNormalizedLabelTZ={isTZAgnosticTMS}
                      />
                    )}

                    {enableOnTimeField && (
                      <div>
                        <Label name='isOnTime'>{'On Time?'}</Label>
                        <Controller
                          name='isOnTime'
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <SelectTrigger className='w-full mt-2'>
                                <SelectValue placeholder='Choose' />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem key={'yes'} value={'yes'}>
                                  Yes
                                </SelectItem>
                                <SelectItem key={'no'} value={'no'}>
                                  No
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`isOnTime`}
                          render={({ message }: { message: string }) => (
                            <Typography
                              variant='body-xs'
                              className='text-error-500'
                            >
                              {message}
                            </Typography>
                          )}
                        />
                      </div>
                    )}

                    {enableReasonField && (
                      <div>
                        <Label
                          name={'reason'}
                          required={!isRelay}
                        >{`${isAljex ? 'EDI Reason' : isRelay ? 'Late Reason' : 'Reason'}`}</Label>
                        <Controller
                          name='reason'
                          control={control}
                          rules={{
                            required: !isRelay ? 'Required' : undefined,
                          }}
                          render={({ field }) => (
                            // TODO: allow user to type and autofill
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={isMilestone && isAljex}
                            >
                              <SelectTrigger className='w-full mt-1'>
                                <SelectValue placeholder='Choose' />
                              </SelectTrigger>
                              <SelectContent>
                                {reasonEnums.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.value}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`reason`}
                          render={({ message }: { message: string }) => (
                            <Typography
                              variant='body-xs'
                              className='text-error-500'
                            >
                              {message}
                            </Typography>
                          )}
                        />
                      </div>
                    )}

                    {enableExceptionField && (
                      <div>
                        <Label name='isException' required>
                          {'Is Exception?'}
                        </Label>
                        <Controller
                          name='isException'
                          control={control}
                          rules={{ required: 'Required' }}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <SelectTrigger className='w-full mt-2'>
                                <SelectValue placeholder='Choose' />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem key={'yes'} value={'yes'}>
                                  Yes
                                </SelectItem>
                                <SelectItem key={'no'} value={'no'}>
                                  No
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`isException`}
                          render={({ message }: { message: string }) => (
                            <Typography
                              variant='body-xs'
                              className='text-error-500'
                            >
                              {message}
                            </Typography>
                          )}
                        />
                      </div>
                    )}

                    <CheckCallTextInput
                      name='notes'
                      label='Notes'
                      readOnly={isMilestone && isAljex}
                    />
                    <Button
                      buttonNamePosthog={ButtonNamePosthog.SubmitCheckCall}
                      type='submit'
                      className='w-full'
                      disabled={loading || skipLoading}
                      data-testid='check-call-submit'
                      logProperties={{
                        loadID: load.ID,
                        freightTrackingID: load.freightTrackingID,
                        serviceID: load.serviceID,
                      }}
                    >
                      {loading ? <ButtonLoader /> : ButtonText.SubmitCheckCall}
                    </Button>

                    {curSuggestion && (
                      <Button
                        buttonNamePosthog={
                          ButtonNamePosthog.SkipCheckCallSuggestion
                        }
                        type='button'
                        className='w-full'
                        variant='outline'
                        disabled={loading || skipLoading}
                        onClick={handleSkipSuggestion}
                        logProperties={{
                          loadID: load.ID,
                          freightTrackingID: load.freightTrackingID,
                          serviceID: load.serviceID,
                        }}
                      >
                        {skipLoading ? (
                          <ButtonLoader />
                        ) : (
                          ButtonText.SkipSuggestion
                        )}
                      </Button>
                    )}
                  </form>
                </div>
              </FormProvider>
            </ExtendedFormProvider>
          </div>
        </LoadSectionAccordionItem>
      )}

      <LoadSectionAccordionItem
        label='Check Call History'
        icon={<RouteIcon className='h-6 w-6' strokeWidth={1} />}
        name='checkCallHistory'
        activeTabs={activeTabs}
      >
        {history ? <Timeline events={history} /> : null}
      </LoadSectionAccordionItem>
    </Accordion>
  );
}
